import { useMutation, useQuery } from '@tanstack/react-query';
import { call } from '@/lib/api';
import { queryClient } from '@/lib/query';

export type TKycStatus = 'valid' | 'expired' | 'missing';

export type TIndividualDetails = {
  fullName: string;
  documentNumber: string;
  nationality: string;
  residenceCountry: string;
  legalAddress: string;
};

export type TCorporateDetails = {
  companyName: string;
  companyRegNumber: string;
  companyTaxId: string;
  companyCountry: string;
  companyLegalAddress: string;
  businessDescription?: string;
  companyWebsite?: string;
};

export type TPayer = {
  id: number;
  accountId: number | null;
  accountName: string | null;
  clientType: 'Individual' | 'Corporate';
  countryCode: string;
  displayName: string;
  kycApprovedAt: string | null;
  kycExpiresAt: string | null;
  kycValid: boolean;
  kycStatus: TKycStatus;
  verifiedEmail: string | null;
  mondayPayerId: number | null;
  mondayKycItemId: number | null;
  deletedAt: string | null;
  createdAt: string;
};

export type TSessionDocument = {
  id: number;
  name: string;
  filename: string;
  createdAt: string;
};

export type TPayerSession = {
  id: number;
  offerId: number | null;
  invoiceId: number | null;
  status: string;
  reviewAnswer: string | null;
  createdAt: string;
  completedAt: string | null;
  sumsubApplicantId: string | null;
  sumsubUrl: string | null;
  sumsubUrlExpiresAt: string | null;
  documents: TSessionDocument[];
};

export type TPayerDetail = Omit<TPayer, 'kycValid' | 'kycStatus'> & {
  clientDetails: Partial<TIndividualDetails & TCorporateDetails> | null;
  sumsubApplicantId: string | null;
  verifiedEmail: string | null;
  delegateContactId: number | null;
  updatedAt: string;
  sessions: TPayerSession[];
};

export const payerKycStatus = (kycExpiresAt: string | null): TKycStatus => {
  if (!kycExpiresAt) return 'missing';
  return new Date(kycExpiresAt) > new Date() ? 'valid' : 'expired';
};

export const keys = {
  all: ['payers'] as const,
  list: (accountId?: number, kycStatus?: string) =>
    [...keys.all, 'list', accountId ?? 'all', kycStatus ?? 'all'] as const,
  detail: (id: number) => [...keys.all, 'detail', id] as const,
};

export const usePayers = (
  accountId?: number,
  kycStatus?: TKycStatus,
  enabled = true,
) =>
  useQuery<TPayer[]>({
    enabled,
    queryKey: keys.list(accountId, kycStatus),
    queryFn: () => {
      const json: Record<string, string> = {};
      if (accountId) json.accountId = String(accountId);
      if (kycStatus) json.kycStatus = kycStatus;
      return call<TPayer[]>('payers', { method: 'GET', json }).then((r) =>
        r.json(),
      );
    },
  });

export const usePayer = (id: number) =>
  useQuery<TPayerDetail>({
    queryKey: keys.detail(id),
    queryFn: () =>
      call<TPayerDetail>('payers/details', {
        method: 'GET',
        json: { id },
      }).then((r) => r.json()),
    enabled: id > 0,
  });

const invalidate = () => queryClient.invalidateQueries({ queryKey: keys.all });

export type TCreatePayerInput = {
  accountId: number;
  clientType: 'Individual' | 'Corporate';
  countryCode: string;
  clientDetails: TIndividualDetails | TCorporateDetails;
};

export const useCreatePayer = () =>
  useMutation({
    mutationFn: (input: TCreatePayerInput) =>
      call<TPayer>('payers/create', { json: input as any }).then((r) =>
        r.json(),
      ),
    onSuccess: invalidate,
  });

export const useUpdatePayer = () =>
  useMutation({
    mutationFn: ({ id, ...rest }: { id: number; displayName?: string }) =>
      call<TPayer>('payers/update', { json: { id, ...rest } as any }).then(
        (r) => r.json(),
      ),
    onSuccess: invalidate,
  });

export const useDeletePayer = () =>
  useMutation({
    mutationFn: (id: number) =>
      call<void>('payers/delete', { json: { id } as any }).then(
        () => undefined,
      ),
    onSuccess: invalidate,
  });

export type TMondayImportCandidate = {
  mondayKycItemId: number;
  mondayContactId: number | null;
  boardId: number;
  source: 'sumsub' | 'manual';
  itemName: string;
  clientDetails: TIndividualDetails;
  countryCode: string;
  verifiedEmail: string | null;
  verifiedEmailStatus: 'resolved' | 'conflicting-label' | 'missing' | 'invalid';
  conflictingEmailLabel: string | null;
  docValidUntil: string | null;
  kycApprovedAt: string;
  kycExpiresAt: string;
  documents: { id: string; name: string; source: 'backoffice' | 'monday' }[];
  missing: string[];
  existingPayerId: number | null;
};

export const useMondayImportPreview = () =>
  useMutation({
    mutationFn: (input: { mondayContactId: number; accountId: number }) =>
      call<TMondayImportCandidate>('payers/import-monday-preview', {
        method: 'GET',
        json: {
          mondayContactId: String(input.mondayContactId),
          accountId: String(input.accountId),
        },
      }).then((r) => r.json()),
  });

export const useImportMondayPayer = () =>
  useMutation({
    mutationFn: (input: {
      mondayKycItemId: number;
      accountId: number;
      mondayContactId?: number;
      acknowledgeEmailConflict?: boolean;
    }) =>
      call<TPayer>('payers/import-monday', { json: input as any }).then((r) =>
        r.json(),
      ),
    onSuccess: invalidate,
  });

export const useSimulateSessionWebhook = () =>
  useMutation({
    mutationFn: (input: { sessionId: number; applicantId: string }) =>
      call<{ success: boolean }>('sumsub/simulate-webhook', {
        json: input as any,
      }).then((r) => r.json()),
    onSuccess: invalidate,
  });

export const useStartPayerKyc = () =>
  useMutation({
    mutationFn: (input: {
      payerId?: number;
      accountId?: number;
      clientType?: string;
      countryCode?: string;
    }) =>
      call<{
        sessionId: number;
        url: string;
        reverify: boolean;
        reused?: boolean;
      }>('payers/kyc-session', { json: input as any }).then((r) => r.json()),
    onSuccess: invalidate,
  });
